'use client';
import { CaptchaRequestMessage, isCaptchaValidationError } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import React from 'react';
import { Send } from 'lucide-react';

import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';

import { Button } from '../ui/button';
import { Heading } from '../ui/heading';

/**
 * Newsletter v pätke — Figma desktop `29:225`, mobil `60042:4854`.
 *
 * Vizuál je prepísaný celý, LOGIKA ostala z pôvodného komponentu jadra:
 * `useMutation(newsletterSubscribe)`, stavy `email` / `isSubscribed` /
 * `isAlreadySubscribed`, odosielanie s `return_url` a meranie cez
 * `trackFormSubmitSuccess`. Hlášky ostávajú z prekladov — je to systémový text,
 * nie dizajnová kópia.
 *
 * Rozmery odmerané z Figmy:
 *   desktop  dva stĺpce po 568, medzi nimi voľný priestor · spodný rám 1px
 *            rgba(255,255,255,0.2) a odsadenie 80 pod ním
 *            pole 52 vysoké, podklad rgba(255,255,255,0.2), placeholder #A9B7CC
 *   mobil    pod sebou · tlačidlo je len ikona, 48×48 (popis je vo Figme skrytý)
 *
 * Zaškrtávacie políčko so súhlasom tu zámerne NIE JE — vo Figme sa nenachádza
 * a David 2026-08-23 rozhodol, že sa má odstrániť a `gdpr` posielať ako `true`.
 * Pôvodný komponent jadra bez zaškrtnutia neodoslal (`if (!checked) return`),
 * preto tá podmienka aj stav `checked` odpadli.
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */
export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const generalLayout = useGlobalStore((s) => s.generalLayout);

  const [email, setEmail] = React.useState('');
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => {
      setIsSubscribed(true);
      trackFormSubmitSuccess({
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: 2,
        send_to: generalLayout.marketing?.gtm_id,
      });
    },
    onError: (captchaRequestError) => { if (isCaptchaValidationError(captchaRequestError)) { setIsAlreadySubscribed(false); return; } setIsAlreadySubscribed(true); },
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: true,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section
      id="newsletters"
      className={cn(
        'ea-homepage-newsletter',
        'flex w-full flex-col gap-8 border-b border-[rgba(255,255,255,0.2)] pb-8',
        'lg:flex-row lg:items-center lg:justify-between lg:gap-16 lg:pb-20',
      )}
    >
      <div className="lg:w-[568px] lg:shrink-0">
        <Heading
          tag="h3"
          as="h2"
          theme="dark"
          title="Potrebujete<br/>prepraviť tovar?"
          description="Zavolajte nám alebo nám nechajte e-mail - ozveme sa s nezáväznou ponukou."
          className={{ description: 'max-w-[448px]' }}
        />
      </div>

      <form
        className={cn('ea-homepage-newsletter__form', 'flex flex-col gap-3 lg:w-[568px]')}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div className="flex items-end gap-2 lg:gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <label htmlFor="newsletter-email" className="label-field text-[#FFFFFF]">
              E-mail
            </label>
            <input
              aria-label={systemLabels('email')}
              id="newsletter-email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('text183')}
              className={cn(
                'h-12 w-full rounded-8 bg-[rgba(255,255,255,0.2)] px-3 lg:h-[52px] lg:px-5',
                'text-[14px] leading-[1.5] tracking-[-0.031em] text-[#FFFFFF] outline-none',
                'placeholder:text-[#A9B7CC] lg:text-[15px] xl:text-[16px]',
                'transition-colors focus:bg-[rgba(255,255,255,0.28)]',
              )}
            />
          </div>

          {/* Figma: na mobile má tlačidlo skrytý popis, ostane z neho štvorec
              s ikonou; od lg je to plné tlačidlo s textom. */}
          <Button
            type="submit"
            variant="brand-accent"
            size="brand"
            rounded="pill"
            disabled={email.length === 0 || isSubscribed}
            isLoading={subscribeMutation.isPending}
            aria-label="Odoslať"
            className="w-12 shrink-0 px-0 lg:w-auto lg:px-8"
          >
            <Send className="size-5 shrink-0" strokeWidth={2} aria-hidden="true" />
            {/* Popis je vo Figme na mobile skrytý — `aria-label` vyššie drží
                prístupný názov tlačidla aj bez neho. */}
            <span className="hidden lg:inline">Odoslať</span>
          </Button>
        </div>

        {isSubscribed && !isAlreadySubscribed && (
          <span className="text-[14px] font-bold text-[#5B9BFF]">{t('text3139')}</span>
        )}
        {isAlreadySubscribed && (
          <span className="text-[14px] font-bold text-[#C42B1C]">{t('text234')}</span>
        )}
      </form>
    </section>
  );
}
